import { Box, Grid, GridItem, Text } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { getConcentrationConfigValueType } from '@finley-technologies/calc-agent';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { ConcentrationLimitCalculationGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { ValueDifferenceGridItem } from './BorrowingBaseSummaryValueDifferenceGridItem';
import {
  borrowingBaseSummaryFormatterMap,
  BorrowingBaseSummaryValueType,
  ExpandedConcentrationLimitCalculations,
  formatExpandedBorrowingBaseReportConcentration,
  getConcentrationLimitConfigMapFromCreditAgreement,
  getDoesConcentrationLimitHaveFilters,
  getFormattedConcentrationLimit,
  getFormattedStringsForDeclarativeFilterOperations,
  getFormattedStringsForWeightedAverageFields,
  getNumberedSubRowLabel,
  getConcentrationLimitConfigCartsTemplateColumns,
  isDeclarativeFilterConcentrationLimitCalculationConfig,
  isPointerMethodCalculationConfig,
  isWeightedAverageConcentrationLimitCalculationConfig,
  BorrowingBaseSummaryInput,
  BorrowingBaseSummaryColumnValueInput,
  getConcentrationLimitRowTemplateColumns,
  getColumnValuesForLoanProductCalculationsField,
  LoanProductCalculationsBorrowingBaseSummaryInput,
  defaultBorrowingBaseSummaryColumnValueInput,
  AllBorrowingBaseReportSummaryFields,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { CalculationTerminologiesPopover } from 'packages/resources/ui/CalculationTerminologiesPopover';

const ConcentrationConfigCard: React.FC<{
  label: string;
  contentLines: string[] | undefined;
  concentrationLimitName: string; // used only for uniq child key
}> = ({ label, contentLines, concentrationLimitName }) => {
  return (
    <GridItem
      borderRadius="8px"
      border={`1px solid ${colors.gray[200]}`}
      padding="16px"
      backgroundColor="white"
      colSpan={1}
    >
      <Text color={colors.black[70]} fontSize="14px">
        {label}
      </Text>
      {!!contentLines &&
        _.map(contentLines, (line, index) => (
          <Text
            key={`${concentrationLimitName}-${label}-${index}`}
            fontWeight={500}
            color={colors.black[95]}
            mt="8px"
          >
            {line ?? '-'}
          </Text>
        ))}
    </GridItem>
  );
};

export const ConcentrationConfigCards: React.FC<{
  concentrationLimitConfig: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  historicalConcentrationLimit?: number;
}> = ({ concentrationLimitConfig, historicalConcentrationLimit }) => {
  const concentrationLimitName = concentrationLimitConfig.name;

  const concentrationLimitCalculationConfig =
    concentrationLimitConfig._config_.concentrationLimitConfig
      ?.calculationConfig;

  const isPointerMethod = isPointerMethodCalculationConfig(
    concentrationLimitCalculationConfig,
  );
  const isDeclarativeFilterCalculationConfig = isDeclarativeFilterConcentrationLimitCalculationConfig(
    concentrationLimitCalculationConfig,
  );
  const isWeightedAverageCalculationConfig = isWeightedAverageConcentrationLimitCalculationConfig(
    concentrationLimitCalculationConfig,
  );
  const hasFilters = getDoesConcentrationLimitHaveFilters(
    concentrationLimitConfig,
  );

  const formattedConcentrationLimitString = getFormattedConcentrationLimit(
    concentrationLimitConfig,
    historicalConcentrationLimit,
  );
  const concentrationReceivablesStrings = isDeclarativeFilterCalculationConfig
    ? getFormattedStringsForDeclarativeFilterOperations(
        concentrationLimitCalculationConfig,
      )
    : undefined;
  const weightedAverageReceivableStrings = isWeightedAverageCalculationConfig
    ? getFormattedStringsForWeightedAverageFields(
        concentrationLimitCalculationConfig,
      )
    : undefined;

  const templateColumns = getConcentrationLimitConfigCartsTemplateColumns({
    isWeightedAverage: isWeightedAverageCalculationConfig,
    hasFilters,
  });

  return (
    <Grid templateColumns={templateColumns} gap="16px" marginBottom="16px">
      <ConcentrationConfigCard
        label="Concentration Limit"
        contentLines={
          formattedConcentrationLimitString
            ? [formattedConcentrationLimitString]
            : undefined
        }
        concentrationLimitName={concentrationLimitName}
      />
      {isPointerMethod && (
        <ConcentrationConfigCard
          label="Concentration Configuration"
          contentLines={['This is a custom concentration method.']}
          concentrationLimitName={concentrationLimitName}
        />
      )}
      {hasFilters && (
        <ConcentrationConfigCard
          label="Concentration Receivables"
          contentLines={concentrationReceivablesStrings}
          concentrationLimitName={concentrationLimitName}
        />
      )}
      {isWeightedAverageCalculationConfig && (
        <ConcentrationConfigCard
          label="Weighted Average Field"
          contentLines={weightedAverageReceivableStrings}
          concentrationLimitName={concentrationLimitName}
        />
      )}
    </Grid>
  );
};

const BorrowingBaseSummaryConcentrationLimitRow: React.FC<{
  label: string;
  columnValues: BorrowingBaseSummaryColumnValueInput;
  valueType: BorrowingBaseSummaryValueType;
  showComparisonAndDifferenceColumns: boolean;
  fieldName: AllBorrowingBaseReportSummaryFields;
}> = ({
  label,
  columnValues,
  valueType,
  showComparisonAndDifferenceColumns,
  fieldName,
}) => {
  const formatter = borrowingBaseSummaryFormatterMap[valueType];

  // value difference calculation and formatting
  const minuend = columnValues.minuendValue;
  const subtrahend = columnValues.subtrahendValue;

  return (
    <>
      {/* If only a single column of values: */}
      {!showComparisonAndDifferenceColumns && (
        <GridItem colSpan={2}>
          {label}
          <Box display="inline-block" ml="4px">
            <CalculationTerminologiesPopover termKeyLabel={label} />
          </Box>
        </GridItem>
      )}
      {!showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1}>
          <Text>{formatter(columnValues.minuendValue)}</Text>
        </GridItem>
      )}

      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1}>
          {label}
          <Box display="inline-block" ml="4px">
            <CalculationTerminologiesPopover termKeyLabel={label} />
          </Box>
        </GridItem>
      )}
      {/* If showing all columns of comparison and difference values: */}
      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.referenceValue)}
          </Text>
        </GridItem>
      )}
      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.subtrahendValue)}
          </Text>
        </GridItem>
      )}
      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.minuendValue)}
          </Text>
        </GridItem>
      )}
      {showComparisonAndDifferenceColumns && (
        // Difference column with special formatting
        <ValueDifferenceGridItem
          subtrahend={subtrahend}
          minuend={minuend}
          valueType={valueType}
          fieldName={fieldName}
        />
      )}
      {showComparisonAndDifferenceColumns && (
        // Placeholder to align with the above accordion dropdown icon
        <GridItem colSpan={1} />
      )}
    </>
  );
};

export const BorrowingBaseSummaryConcentrationLimit: React.FC<{
  concentrationLimitConfig?: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  expandedConcentrationLimitCalculations?: {
    minuend?: ExpandedConcentrationLimitCalculations;
    subtrahend?: ExpandedConcentrationLimitCalculations;
    reference?: ExpandedConcentrationLimitCalculations;
  };
  totalConcentrationKickout: {
    minuend?: number;
    subtrahend?: number;
    reference?: number;
  };
  showComparisonAndDifferenceColumns: boolean;
}> = ({
  concentrationLimitConfig,
  expandedConcentrationLimitCalculations,
  totalConcentrationKickout,
  showComparisonAndDifferenceColumns,
}) => {
  if (!concentrationLimitConfig || !expandedConcentrationLimitCalculations) {
    return null;
  }

  const config = concentrationLimitConfig?._config_.concentrationLimitConfig;
  const concentrationLimitValueType = getConcentrationConfigValueType(config);

  return (
    <Box>
      <ConcentrationConfigCards
        concentrationLimitConfig={concentrationLimitConfig}
        historicalConcentrationLimit={
          expandedConcentrationLimitCalculations.minuend?.concentrationLimit
        }
      />
      <Grid
        templateColumns={getConcentrationLimitRowTemplateColumns({
          showComparisonAndDifferenceColumns,
        })}
        gap="16px"
        marginBottom="16px"
      >
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration Amount"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend
                ?.concentrationAmount,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend
                ?.concentrationAmount,
            referenceValue:
              expandedConcentrationLimitCalculations.reference
                ?.concentrationAmount,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          fieldName="concentrationAmount"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Total Eligible Exposure Less Concentration Kickouts"
          columnValues={{
            minuendValue: expandedConcentrationLimitCalculations.minuend
              ?.totalEligibleExposure
              ? expandedConcentrationLimitCalculations.minuend
                  .totalEligibleExposure -
                (totalConcentrationKickout.minuend ?? 0)
              : undefined,
            subtrahendValue: expandedConcentrationLimitCalculations.subtrahend
              ?.totalEligibleExposure
              ? expandedConcentrationLimitCalculations.subtrahend
                  .totalEligibleExposure -
                (totalConcentrationKickout.subtrahend ?? 0)
              : undefined,
            referenceValue: expandedConcentrationLimitCalculations.reference
              ?.totalEligibleExposure
              ? expandedConcentrationLimitCalculations.reference
                  .totalEligibleExposure -
                (totalConcentrationKickout.reference ?? 0)
              : undefined,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          fieldName="totalEligibleExposureLessConcentrationKickouts"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend?.concentration,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend?.concentration,
            referenceValue:
              expandedConcentrationLimitCalculations.reference?.concentration,
          }}
          valueType={concentrationLimitValueType}
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          fieldName="concentration"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration Limit Amount"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend
                ?.concentrationLimitAmount,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend
                ?.concentrationLimitAmount,
            referenceValue:
              expandedConcentrationLimitCalculations.reference
                ?.concentrationLimitAmount,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          fieldName="concentrationLimitAmount"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration Kickout"
          columnValues={{
            minuendValue: totalConcentrationKickout.minuend,
            subtrahendValue: totalConcentrationKickout.subtrahend,
            referenceValue: totalConcentrationKickout.reference,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          fieldName="totalConcentrationKickout"
        />
      </Grid>
    </Box>
  );
};

export const BorrowingBaseSummaryConcentrationLimitAccordion: React.FC<{
  loanProductCalculations: LoanProductCalculationsBorrowingBaseSummaryInput;
  borrowingBaseReports: BorrowingBaseSummaryInput;
  concentrationLimitCalculationsForLoanProduct: {
    minuendBorrowingBaseReport: ConcentrationLimitCalculationGQLType[];
    subtrahendBorrowingBaseReport?: ConcentrationLimitCalculationGQLType[];
    referenceBorrowingBaseReport?: ConcentrationLimitCalculationGQLType[];
  };
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  loanProductId?: string;
  loanProductLabel: string;
  showComparisonAndDifferenceColumns: boolean;
  defaultIsExpanded: boolean;
}> = ({
  loanProductCalculations,
  creditAgreement,
  borrowingBaseReports,
  concentrationLimitCalculationsForLoanProduct,
  loanProductId,
  loanProductLabel,
  showComparisonAndDifferenceColumns,
  defaultIsExpanded,
}) => {
  const concentrationLimitConfigMap = getConcentrationLimitConfigMapFromCreditAgreement(
    creditAgreement,
  );

  const totalConcentrationKickoutForLps = loanProductId
    ? getColumnValuesForLoanProductCalculationsField({
        loanProductCalculationsForBorrowingBaseReports: loanProductCalculations,
        loanProductId,
        fieldName: 'totalConcentrationKickout',
      })
    : defaultBorrowingBaseSummaryColumnValueInput;

  const hasConcentrationLimitCalculationsForLoanProduct =
    !!concentrationLimitCalculationsForLoanProduct.minuendBorrowingBaseReport
      .length ||
    !!concentrationLimitCalculationsForLoanProduct.subtrahendBorrowingBaseReport
      ?.length ||
    !!concentrationLimitCalculationsForLoanProduct.referenceBorrowingBaseReport
      ?.length;

  return (
    <Box>
      {!hasConcentrationLimitCalculationsForLoanProduct && (
        <BorrowingBaseSummaryAccordion
          accordionButton={({ hasAccordionPanel, isExpanded }) => (
            <BorrowingBaseSummaryAccordionRow
              label={loanProductLabel}
              columnValues={totalConcentrationKickoutForLps}
              valueType="currency"
              hasAccordionPanel={hasAccordionPanel}
              isExpanded={isExpanded}
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
              fieldName="totalConcentrationKickout"
            />
          )}
        />
      )}

      {hasConcentrationLimitCalculationsForLoanProduct && (
        <BorrowingBaseSummaryAccordion
          defaultIsExpanded={defaultIsExpanded}
          accordionButton={({ hasAccordionPanel, isExpanded }) => (
            <BorrowingBaseSummaryAccordionRow
              label={loanProductLabel}
              columnValues={totalConcentrationKickoutForLps}
              valueType="currency"
              hasAccordionPanel={hasAccordionPanel}
              isExpanded={isExpanded}
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
              fieldName="totalConcentrationKickout"
            />
          )}
          accordionPanel={
            <>
              {_.map(
                concentrationLimitCalculationsForLoanProduct.minuendBorrowingBaseReport,
                (calc, index) => {
                  const {
                    borrowingBaseReduction,
                    concentrationLimitKey,
                  } = calc;
                  const concentrationLimitConfig = concentrationLimitConfigMap.get(
                    concentrationLimitKey,
                  );

                  const totalConcentrationKickoutMinuend = borrowingBaseReduction;
                  const totalConcentrationKickoutSubtrahend = _.find(
                    concentrationLimitCalculationsForLoanProduct.subtrahendBorrowingBaseReport,
                    (subtrahendCalcs) =>
                      subtrahendCalcs.concentrationLimitKey ===
                      concentrationLimitKey,
                  )?.borrowingBaseReduction;
                  const totalConcentrationKickoutReference = _.find(
                    concentrationLimitCalculationsForLoanProduct.referenceBorrowingBaseReport,
                    (referenceCalcs) =>
                      referenceCalcs.concentrationLimitKey ===
                      concentrationLimitKey,
                  )?.borrowingBaseReduction;

                  const expandedConcentrationLimitCalculationsMinuend = formatExpandedBorrowingBaseReportConcentration(
                    {
                      borrowingBaseReport:
                        borrowingBaseReports.minuendBorrowingBaseReport,
                      concentrationLimitKey,
                      concentrationLimitConfig,
                    },
                  );

                  const expandedConcentrationLimitCalculationsSubtrahend = formatExpandedBorrowingBaseReportConcentration(
                    {
                      borrowingBaseReport:
                        borrowingBaseReports.subtrahendBorrowingBaseReport,
                      concentrationLimitKey,
                      concentrationLimitConfig,
                    },
                  );

                  const expandedConcentrationLimitCalculationsReference = formatExpandedBorrowingBaseReportConcentration(
                    {
                      borrowingBaseReport:
                        borrowingBaseReports.referenceBorrowingBaseReport,
                      concentrationLimitKey,
                      concentrationLimitConfig,
                    },
                  );

                  const concentrationLimitKeyLabel = getNumberedSubRowLabel(
                    index,
                    concentrationLimitKey,
                  );

                  return (
                    <Grid
                      templateColumns="repeat(5, 1fr)"
                      gap="16px"
                      key={`concentration-limit-accordion-${concentrationLimitKey}`}
                    >
                      <GridItem colSpan={5}>
                        <BorrowingBaseSummaryAccordion
                          accordionButton={({
                            hasAccordionPanel,
                            isExpanded,
                          }) => (
                            <BorrowingBaseSummaryAccordionRow
                              label={concentrationLimitKeyLabel}
                              columnValues={
                                showComparisonAndDifferenceColumns
                                  ? {
                                      minuendValue: totalConcentrationKickoutMinuend,
                                      subtrahendValue: totalConcentrationKickoutSubtrahend,
                                      referenceValue: totalConcentrationKickoutReference,
                                    }
                                  : {
                                      minuendValue: totalConcentrationKickoutMinuend,
                                    }
                              }
                              valueType="currency"
                              hasAccordionPanel={hasAccordionPanel}
                              isExpanded={isExpanded}
                              showComparisonAndDifferenceColumns={
                                showComparisonAndDifferenceColumns
                              }
                              fieldName="totalConcentrationKickout"
                            />
                          )}
                          accordionPanel={
                            <BorrowingBaseSummaryConcentrationLimit
                              concentrationLimitConfig={
                                concentrationLimitConfig
                              }
                              expandedConcentrationLimitCalculations={{
                                minuend: expandedConcentrationLimitCalculationsMinuend,
                                subtrahend: expandedConcentrationLimitCalculationsSubtrahend,
                                reference: expandedConcentrationLimitCalculationsReference,
                              }}
                              totalConcentrationKickout={{
                                minuend: totalConcentrationKickoutMinuend,
                                subtrahend: totalConcentrationKickoutSubtrahend,
                                reference: totalConcentrationKickoutReference,
                              }}
                              showComparisonAndDifferenceColumns={
                                showComparisonAndDifferenceColumns
                              }
                            />
                          }
                        />
                      </GridItem>
                    </Grid>
                  );
                },
              )}
            </>
          }
        />
      )}
    </Box>
  );
};
